import { Injectable, inject } from '@angular/core';
import { App } from '@df/core/app';
import { Url } from '@df/core/service/url';
import { IResizeOptions, ResizeService } from '@df/ui/common/resize.service';
import defaults from 'lodash-es/defaults';
import { NAkamai } from './akamai.interface';

/**
 * Documentation with URL image parameters
 * https://techdocs.akamai.com/ivm/docs/imquery
 */
@Injectable({
    providedIn: 'root'
})
export class AkamaiResizeService extends ResizeService {
    protected app = inject(App);
    protected url = inject(Url);
    protected akamaiConfig = inject(NAkamai.OPTIONS);

    /**
     * Image loader for `DfImageDirective`
     *
     * @param config {NAkamai.ILoaderConfig}
     * @returns {string}
     *
     * @link https://angular.dev/guide/image-optimization#configuring-an-image-loader-for-ngoptimizedimage
     */
    loader(config: NAkamai.ILoaderConfig): string {
        if (!config.src) {
            return '';
        }

        if (config.src?.match(/\.(svg|gif)$/)) {
            return this.url.toMediaUrl(config.src);
        }

        if (config.width) {
            config.loaderParams = config.loaderParams || ({} as IResizeOptions);
            config.loaderParams.width = config.width;
        }

        return this.resize(this.url.toMediaUrl(config.src), config.loaderParams);
    }

    /**
     * Modifies the image URL with the appropriate resize parameters
     *
     * @param imageUrl {string} The URL of the image
     * @param options {IResizeOptions} Resize options
     * @returns {string} The modified image URL
     */
    resize(imageUrl: string, options: IResizeOptions): string {
        defaults(options, this.akamaiConfig.defaultOptions);

        const args: string[] = [];

        // Set the background color
        if (options?.fillColor) {
            if (options.fillColor === 'transparent') {
                // do nothing
            } else {
                if (options.fillColor === 'white') {
                    args.push('BackgroundColor,color=FFFFFF');
                } else {
                    args.push('BackgroundColor,color=' + options.fillColor.replace('#', ''));
                }
            }
        }

        // Set the width and height of the image
        if (options?.width !== undefined && options?.height !== undefined) {
            args.push(`Resize,width=${options?.width}`);

            if (options?.width > 0) {
                args.push(`Crop,width=${options?.width},height=${options?.height},gravity=Center`);
            }
        } else if (options?.width !== undefined) {
            args.push('Resize,width=' + options?.width);
        }

        // Set the fit option for the image
        if (options?.fitIn && options.fitIn !== '0') {
            args.push('FitAndFill');
        }

        // Trim the image
        if (options?.trim && options.trim !== '0') {
            if ([1, true, 'true', '1'].indexOf(options.trim) !== -1) {
                args.push('Trim');
            } else {
                args.push('Trim,fuzz=' + options.trim);
            }
        }

        // Crops an image
        if (options?.crop) {
            args.push(`Crop,${options.crop}`);
        }

        // Applies a method to detect faces in the source image and applies the rectangular crop on either the biggest face or all of the faces detected.
        if (options?.faceCrop) {
            args.push('FaceCrop');
        }

        return imageUrl + '?im=' + args.join(';');
    }
}
